import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  Button,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FiEye } from "react-icons/fi";
import { Header } from "build-ts/metadata";
import {
  getScheduledJobRunsByJobId,
  deleteScheduledJobRun,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import AnalysisRunCard from "containers/schedule/AnalysisRunCard";

interface AnalysisRunsViewerProps {
  selectedJob: Header | undefined;
}

const AnalysisRunsViewer: React.FC<AnalysisRunsViewerProps> = ({
  selectedJob
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [jobRuns, setJobRuns] = useState<Header[]>([]);
  const [viewOutputsExpanded, setViewOutputsExpanded] = useState(false);

  const fetchJobRuns = async (selectedJobId: string) => {
    setIsLoading(true);
    const response = await getScheduledJobRunsByJobId(selectedJobId);
    if (response) {
      setJobRuns(response);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedJob?.id) {
      fetchJobRuns(selectedJob?.id);
    }
  }, [selectedJob?.id]);

  const handleRefresh = async () => {
    if (!selectedJob?.id) return;
    setIsRefreshing(true);
    await fetchJobRuns(selectedJob.id);
    setIsRefreshing(false);
    showToast("Analysis runs refreshed successfully", "", "success");
  };

  const handleDeleteRun = async (runId: string) => {
    const result = await deleteScheduledJobRun(runId);
    if (result) {
      setJobRuns((prev) => prev.filter((run) => run.id !== runId));
      showToast("Analysis run deleted successfully", "", "success");
      fetchJobRuns(selectedJob?.id || "");
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
        <Text mt={4}>Loading analysis runs...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={2} align="stretch" h="100%">
      <Flex align="center" justify="space-between" mb={3} px={1}>
        <Text fontSize="sm" color="gray.500" fontWeight="600" align="left" m={0}>
          {jobRuns.length > 0 ? `Showing ${jobRuns.length} run${jobRuns.length !== 1 ? "s" : ""}` : "No runs found"}
        </Text>
        <HStack spacing={2}>
          <Tooltip label="Refresh runs" placement="top">
            <Button
              leftIcon={<RepeatIcon />}
              size="sm"
              variant="outline"
              colorScheme="gray"
              isLoading={isRefreshing}
              onClick={handleRefresh}
              fontWeight="600"
              iconSpacing={0}
            >
            </Button>
          </Tooltip>
          {jobRuns.length > 0 && (
            <Button
              leftIcon={<Icon as={FiEye} boxSize={5} />}
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={() => setViewOutputsExpanded(prev => !prev)}
              fontWeight="700"
            >
              {viewOutputsExpanded ? "Hide Outputs" : "View Outputs"}
            </Button>
          )}
        </HStack>
      </Flex>
      {jobRuns.length === 0 ? (
        <Alert status="info" bg="gray.50" border="1px" borderColor="purple.600" color="purple.700" fontWeight="600">
          <AlertIcon color="purple.700" />
          No analysis runs found for this scheduled job.
        </Alert>
      ) : (
        <VStack spacing={2} align="stretch" h="100%">
          <Box
            flex={1}
            overflowY="auto"
            maxH="600px"
            pr={2}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            <VStack spacing={2} align="stretch">
              {jobRuns?.map((jobRun) => (
                <AnalysisRunCard
                  key={jobRun.id}
                  jobRun={jobRun}
                  onDelete={() => {
                    handleDeleteRun(jobRun?.id || "");
                  }}
                  expandAllOutputs={viewOutputsExpanded}
                />
              ))}
            </VStack>
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

export default AnalysisRunsViewer;
